import { httpClient } from '@/shared/api/httpClient';
import type { IDeviceRepository } from '../../domain/repositories/IDeviceRepository';
import type { Device, DeviceMaster } from '../../domain/entities/Device';
import type { EnrollDeviceParams } from '../../domain/value-objects/EnrollDeviceParams';
import type { DeviceDTO, DeviceMasterDTO, EnrollDeviceResponseDTO } from '../dto/DeviceDTO';
import type { EnrollDeviceRequestDTO } from '../dto/EnrollDeviceRequestDTO';
import { toDevice, toDeviceMaster, toEnrollDeviceRequestDTO } from '../mappers/deviceMapper';
import { IdDevice } from '@/shared/domain/value-objects/IdDevice';
import type { ApiError } from '@/shared/api/ApiError';
import { DeviceEnrollmentFailedError, LicenseExceededError, LicenseNotFoundError, TimezoneRejectedError } from '../../domain/errors/DeviceErrors';

export class RemoteDeviceRepository implements IDeviceRepository {
  async findById(idDevice: number): Promise<Device | null> {
    const safeId = IdDevice.from(idDevice);
    try {
      const res = await httpClient.get<{ data: { result: DeviceDTO } }>(
        `/v1/clock/app/${safeId}/id/device`,
      );
      return toDevice(res.data.data.result);
    } catch (err) {
      const apiErr = err as ApiError;
      if (apiErr.kind === 'not_found') return null;
      if (apiErr.kind === 'business' && apiErr.code === 'license_clock_exceeded') {
        throw new LicenseExceededError();
      }
      if (apiErr.kind === 'business' && apiErr.code === 'license_clock_not_found') {
        throw new LicenseNotFoundError();
      }
      throw apiErr;
    }
  }

  async findMaster(idDevice: number): Promise<DeviceMaster | null> {
    const safeId = IdDevice.from(idDevice);
    try {
      const res = await httpClient.get<{ data: { result: DeviceMasterDTO } }>(
        `/v1/devices_master/${safeId}/search`,
      );
      return toDeviceMaster(res.data.data.result);
    } catch (err) {
      const apiErr = err as ApiError;
      if (apiErr.kind === 'not_found') return null;
      throw apiErr;
    }
  }

  async lookupByIdUnique(idUnique: string): Promise<Device | null> {
    try {
      const res = await httpClient.get<DeviceDTO>(
        `/v1/clock/app/${idUnique}/id/device`,
      );
      return toDevice(res.data);
    } catch (err) {
      const apiErr = err as ApiError;
      if (apiErr.kind === 'not_found') return null;
      throw new DeviceEnrollmentFailedError(apiErr.message);
    }
  }

  /**
   * Enrolls this device with the backend.
   * POST /v1/clock/app/enroll/device → 201 on first enroll, 204 on duplicate.
   *
   * NOTE: The backend has an HTTP/2 END_STREAM bug — it sends the complete body
   * in ~1s but keeps the stream open until keep-alive timeout (~60-70s).
   * The httpClient timeout is set to 120s as a workaround (see httpClient.ts).
   * Real fix: H2OutputBuffering off in Apache, or ob_end_flush() in PHP.
   * See: docs/bug-report-enroll-device-http2-end-stream.md
   */
  async enroll(params: EnrollDeviceParams): Promise<Device> {
    const dto: EnrollDeviceRequestDTO = toEnrollDeviceRequestDTO(params);
    try {
      const res = await httpClient.post<EnrollDeviceResponseDTO>(
        '/v1/clock/app/enroll/device',
        dto,
        { validateStatus: (s) => s === 201 || s === 204 },
      );
      if (res.status === 201) {
        return toDevice(res.data.data.result);
      }
      // 204: device already existed — recover via GET
      const existing = await this.lookupByIdUnique(params.idUnique);
      if (existing) return existing;
      throw new DeviceEnrollmentFailedError('Device registered (204) but lookup returned null');
    } catch (err) {
      if (err instanceof DeviceEnrollmentFailedError) throw err;
      if (err instanceof LicenseExceededError) throw err;
      
      const apiErr = err as ApiError;
      
      // Check for LICENSE_CLOCK_EXCEEDED error (classified as business error by classifyApiError)
      if (apiErr.kind === 'business' && apiErr.code === 'license_clock_exceeded') {
        throw new LicenseExceededError();
      }

      // timezone_pg validation error (e.g. { status: '110', message: [{ timezone_pg: 'if118' }] })
      if (apiErr.kind === 'validation' && apiErr.fields?.['timezone_pg']) {
        throw new TimezoneRejectedError(params.timezone);
      }

      throw new DeviceEnrollmentFailedError(apiErr.message ?? 'Error al registrar dispositivo');
    }
  }
}
